import {
  readLocalPreference,
  writeLocalPreference,
} from '@/lib/local-preference'
import type { LibraryItem } from './filter-engine'

/**
 * The library's tri-state seen filter (LG-362 §8). One pill, click cycles
 * the states in ticket order; 'unseen' is the cold-start default and 'all'
 * means the filter is effectively off.
 */
export type SeenFilter = 'unseen' | 'all' | 'seen'

const SEEN_FILTER_ORDER: readonly SeenFilter[] = ['unseen', 'all', 'seen']

export const SEEN_FILTER_LABEL: Record<SeenFilter, string> = {
  unseen: 'Unseen only',
  all: 'Seen + unseen',
  seen: 'Seen only',
}

/**
 * Persisted mode's overlay status, surfaced on the curriculum slice (both
 * absent in mock mode, which has no query — plain truthiness is the correct
 * check everywhere): `seenOverlayPending` suspends the pill's filter until
 * the per-client map lands, so the table never flashes "everything is
 * unseen"; `seenOverlayFailed` means the query died and the library must
 * disclose it — an amber "Unseen only" pill over an unfiltered table is a
 * silent lie otherwise.
 */
export interface SeenOverlayStatus {
  seenOverlayPending?: boolean
  seenOverlayFailed?: boolean
}

export function nextSeenFilter(current: SeenFilter): SeenFilter {
  const index = SEEN_FILTER_ORDER.indexOf(current)
  return SEEN_FILTER_ORDER[(index + 1) % SEEN_FILTER_ORDER.length]
}

/**
 * Whether a row counts as seen. The merged overlay flag (persisted mode) is
 * authoritative when present; mock rows fall back to their `repeat` text,
 * which is that mode's stand-in for history.
 */
export function itemIsSeen(item: LibraryItem): boolean {
  return item.seen ?? item.repeat != null
}

/**
 * A coach-level viewing preference, like the column layout — deliberately
 * NOT per-client, so it survives switching clients. The stored value wins on
 * load; 'unseen' is only the value when nothing is stored yet.
 */
const SEEN_FILTER_STORAGE_KEY = 'bctci:curriculum:seen-filter'

const isSeenFilter = (value: string): value is SeenFilter =>
  (SEEN_FILTER_ORDER as readonly string[]).includes(value)

export const readStoredSeenFilter = (): SeenFilter =>
  readLocalPreference<SeenFilter>(
    SEEN_FILTER_STORAGE_KEY,
    (raw) => (isSeenFilter(raw) ? raw : null),
    'unseen',
  )

export const writeStoredSeenFilter = (value: SeenFilter): void =>
  writeLocalPreference(SEEN_FILTER_STORAGE_KEY, value)
